import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/format";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Optional leading icon slot. */
  icon?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  // Bright fill, ink text — the single loud button on any screen.
  primary:
    "bg-bright text-ink border border-bright hover:bg-bright/90 disabled:hover:bg-bright font-medium",
  // 1px line border, no fill — the workhorse.
  secondary:
    "bg-transparent text-body border border-line hover:border-muted hover:text-bright disabled:hover:border-line disabled:hover:text-body",
  // Desaturated red — information, not decoration.
  danger:
    "bg-transparent text-danger border border-danger/60 hover:bg-danger/10 hover:border-danger disabled:hover:bg-transparent",
  // Borderless — dense rows, toolbars.
  ghost:
    "bg-transparent text-muted border border-transparent hover:text-bright hover:bg-surface-raised",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-xs gap-1.5",
  md: "h-9 px-4 text-sm gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading = false, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? "button"}
      disabled={disabled || loading}
      className={cx(
        "inline-flex select-none items-center justify-center whitespace-nowrap rounded transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" className={variant === "primary" ? "text-ink" : undefined} /> : icon}
      {children}
    </button>
  );
});
